import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import type { RefObject } from 'react';

export interface FrameStatsValue {
  fps: number;
  frameMs: number;
}

export function createFrameStats(): FrameStatsValue {
  return { fps: 0, frameMs: 0 };
}

/**
 * FPS 측정.
 *
 * 프레임마다 setState하면 측정이 그 자체로 프레임을 갉아먹으므로 ref에만 씁니다.
 * HUD는 이미 돌고 있는 자기 rAF 루프에서 이 값을 읽습니다.
 *
 * 0.5초 창으로 평균을 냅니다. 프레임 하나마다 갱신하면 숫자가 요동쳐서 읽을 수 없습니다.
 */
export function FrameStats({ statsRef }: { statsRef: RefObject<FrameStatsValue> }) {
  /*
   * 누적값을 지역 변수로 두면 안 됩니다. R3F는 최신 콜백 클로저를 호출하므로 이 컴포넌트가
   * 다시 렌더될 때마다 창이 0으로 리셋되어 FPS가 튑니다.
   */
  const window = useRef({ frames: 0, elapsed: 0 });

  useFrame((_, delta) => {
    const accumulator = window.current;
    accumulator.frames += 1;
    accumulator.elapsed += delta;
    if (accumulator.elapsed < 0.5) {
      return;
    }
    const stats = statsRef.current;
    if (stats !== null) {
      stats.fps = Math.round(accumulator.frames / accumulator.elapsed);
      stats.frameMs =
        Math.round((accumulator.elapsed / accumulator.frames) * 1000 * 10) / 10;
    }
    accumulator.frames = 0;
    accumulator.elapsed = 0;
  });

  return null;
}
